import { useCallback, useMemo } from 'react';
import { getPurchaseStatus } from '@/SearchResults/ResultTile/ResultTile.helpers';
import { useConfiguration } from '~/contexts/StaticContext';
import { useUserLocation } from '~/contexts/UserContext';
import { Vehicle } from '~/contracts';

export enum PriceDropType {
  NewPrice = 'New Price',
  PriceDrop = 'Price Drop'
}

export function getPriceDropValue(vehicle: Partial<Vehicle>) {
  return vehicle.previousPrice && vehicle.price?.total
    ? vehicle.previousPrice - vehicle.price.total
    : 0;
}

function usePriceDrop() {
  const { state = '' } = useUserLocation();
  const { priceDropRestrictedStates } = useConfiguration();
  const restrictedState = priceDropRestrictedStates.includes(state);

  const bannerText = useMemo(
    () =>
      restrictedState
        ? 'We’ve updated prices on select vehicles, don’t miss out!'
        : 'We’ve lowered prices to give you more savings. Don’t miss out!',
    [restrictedState]
  );

  const displayText = useMemo(
    () => (restrictedState ? PriceDropType.NewPrice : PriceDropType.PriceDrop),
    [restrictedState]
  );

  const getPriceDrop = useCallback((vehicle: Partial<Vehicle>) => {
    const { isLocked, isOnHold } = getPurchaseStatus(vehicle);
    const priceDrop = getPriceDropValue(vehicle);

    const showPriceDrop = Boolean(
      vehicle.previousPrice && priceDrop > 0 && !isLocked && !isOnHold
    );

    return {
      priceDrop,
      showPriceDrop
    };
  }, []);

  return {
    displayText,
    bannerText,
    getPriceDrop,
    restrictedState
  };
}

export default usePriceDrop;
